import React, { useEffect } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { hasPageKey } from '../utils/pageKeys';
import { landingPath } from '../utils/landingPath';
import { useTranslation } from 'react-i18next';

const pathToPageKey: Record<string, string> = {
  '/dashboard': 'dashboard',
  '/messages': 'messages',
  '/wa-business/numbers': 'wa-business-numbers',
  '/wa-business/ai-assistant': 'wa-business-ai-assistant',
  '/social/inbox': 'social-inbox',
  '/social/connections': 'social-connections',
  '/social/ai-assistant': 'social-ai-assistant',
  '/social/comments': 'social-comments',
  '/templates': 'templates-hub',
  '/waha': 'waha',
  '/waha/chat': 'waha-chat',
  '/waha/contacts': 'waha-contacts-hub',
  '/waha/bulk': 'waha-bulk',
  '/waha/departments': 'waha-departments',
  '/waha/group-tracker': 'group-tracker',
  '/waha/automation': 'waha-automation',
  '/waha/ai-assistant': 'waha-ai-assistant',
  '/waha/support-tickets': 'waha-conversations',
  '/bug-reports': 'bug-reports',
  '/whatsapp-contacts': 'whatsapp-contacts',
  '/campaigns': 'campaigns',
  '/email-campaigns': 'email-campaigns',
  '/email-lists': 'email-lists',
  // Reached from E-posta Kampanyaları; governed by the same permission
  '/email-templates': 'email-campaigns',
  '/sms-campaigns': 'sms-campaigns',
  '/google-ads': 'google-ads',
  '/google-ads/campaigns': 'google-ads-campaigns',
  '/google-ads/ad-groups': 'google-ads-ad-groups',
  '/google-ads/keywords': 'google-ads-keywords',
  '/google-ads/ads': 'google-ads-ads',
  '/google-ads/conversions': 'google-ads-conversions',
  '/google-ads/extensions': 'google-ads-extensions',
  '/google-ads/search-terms': 'google-ads-search-terms',
  '/google-ads/reports': 'google-ads-reports',
  '/google-ads/dashboards': 'google-ads-dashboards',
  '/google-ads/anomalies': 'google-ads-anomalies',
  '/google-ads/alerts': 'google-ads-anomalies',
  '/google-ads/url-health': 'google-ads-url-health',
  '/google-ads/insights': 'google-ads-insights',
  '/google-ads/budget-pacing': 'google-ads-budget-pacing',
  '/google-ads/assistant': 'google-ads-assistant',
  '/google-ads/ai-reports': 'google-ads-ai-reports',
  '/google-ads/ai-analysis': 'google-ads-ai-analysis',
  '/analiz-leads': 'analiz-leads',
  '/media': 'media',
  '/customers': 'customers',
  '/requests': 'requests',
  '/analytics': 'analytics',
  '/users': 'users-hub',
  '/activity-logs': 'activity-logs',
  '/settings': 'settings',
  '/mail/dashboard': 'mail-dashboard',
  '/mail/domains': 'mail-domains',
  '/mail/accounts': 'mail-accounts',
  '/mail/delivery': 'mail-queue',
  '/mail/customer-packages': 'mail-allocations',
  '/mail/infrastructure': 'mail-bayi-quotas',
  '/ai-assistant/management': 'ai-assistant-management',
  // Old WhatsApp URLs (they redirect to a tab of the pages above): checked with their own key
  '/templates/submission': 'templates-submission',
  '/waha/groups': 'waha-groups',
  '/waha/department-messages': 'waha-department-messages',
  '/waha/all-messages': 'waha-all-messages',
  '/waha/support-tickets-trash': 'waha-support-tickets-trash',
  '/waha/welcome-messages': 'waha-welcome-messages',
  '/waha/chatbot/settings': 'waha-chatbot-settings',
  '/waha/chatbot/rules': 'waha-chatbot-rules',
  '/waha/chatbot/flows': 'waha-chatbot-flows',
  '/waha/quick-replies': 'waha-quick-replies',
  '/waha/mentions': 'waha-mentions',
};

interface PrivateRouteProps {
  children: React.ReactElement;
  allowedRoles?: string[];
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, allowedRoles }) => {
  const { t } = useTranslation();
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user && user.role === 'bayi') {
      const checkSubscription = () => {
        if (user.subscription_end_date) {
          const endDate = new Date(user.subscription_end_date);
          const now = new Date();

          if (now > endDate) {
            logout();
            localStorage.setItem('subscription_expired', JSON.stringify({
              username: user.username,
              endDate: user.subscription_end_date,
              message: t('Abonelik süreniz dolmuştur')
            }));
            navigate('/login');
          }
        }
      };

      checkSubscription();
      const interval = setInterval(checkSubscription, 60000);
      return () => clearInterval(interval);
    }
  }, [user, logout, navigate]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // allowed_pages check
  const userAllowedPages = (user as any)?.allowed_pages as string[] | null;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={landingPath(user)} replace />;
  }

  // Links saved when the AI assistant was a tab of Otomasyon; redirected before the page check,
  // which would otherwise judge them by the Otomasyon permission
  if (location.pathname === '/waha/automation' && new URLSearchParams(location.search).get('tab') === 'ai-assistant') {
    return <Navigate to="/waha/ai-assistant" replace />;
  }

  if (userAllowedPages && userAllowedPages.length > 0) {
    const currentPath = location.pathname;
    // Exact match or the closest parent path (e.g.: /waha/chat/xxx -> /waha/chat, not /waha)
    const matchedKey = Object.entries(pathToPageKey)
      .filter(([path]) => currentPath === path || currentPath.startsWith(path + '/'))
      .sort((a, b) => b[0].length - a[0].length)[0];
    if (matchedKey) {
      const pageKey = matchedKey[1];
      if (!hasPageKey(userAllowedPages, pageKey)) {
        return <Navigate to={landingPath(user)} replace />;
      }
    }
  }

  return children;
};

export default PrivateRoute;